import * as React from 'react';
import { Route, Switch, RouteComponentProps } from 'react-router-dom';
import Tour24FaqsPage from './public/Tour24FaqsPage';
import PropertiesPage from './public/properties/PropertiesPage';
import MyProfilePage from './account/MyProfilePage';
import { ProspectBasePath, propertySlugCapture, tourGuidCapture } from './ProspectRouteValues';
import MyToursPage from './tour/allTours/MyToursPage';
import ProspectIndexPage from './ProspectIndexPage';
import '../../../styles/ProspectUser.css';
import '../../../styles/ProspectWorkflow.css';
import '../../../styles/MyToursPage.css';
import '../../../styles/MyProfilePage.css';
import '../../../styles/Search.css';
import LoginPage from './public/LoginPage';
import MyPreferencesPage from './account/MyPreferencesPage';
import WelcomePage from './public/properties/WelcomePage';
import RegistrationPage from './public/RegistrationPage';
import PasswordResetPage from './public/PasswordResetPage';
import AuthorizedRoute from '../../common/AuthorizedRoute';
import { Roles } from '../../../utilities/Enums';
import FaqPage from '../FaqPage';
import AskPage from './public/AskPage';
import * as queryString from 'query-string';
import { getSourceData, setSourceData, SourceData } from '../../../utilities/sourceData';
import ProspectLayout from '../../layouts/ProspectLayout';
import HelpPage from './public/HelpPage';
import AboutUsPage from './public/AboutUsPage';
import WebTourFaqsPage from './public/WebTourFaqsPage';
import StartTourPage from './tour/applessTour/StartTourPage';
import UnitMapView from './public/properties/UnitMapView';
import PropertyMapPage from './public/properties/PropertyMapPage';

export interface ProspectPage {
    extension: string;
    component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
    displayText?: string;
    icon?: string;
}

export const prospectPages: {
    welcome: ProspectPage;
    properties: ProspectPage;
    tour24Faq: ProspectPage;
    webTourFaq: ProspectPage;
    profile: ProspectPage;
    tours: ProspectPage;
    preferences: ProspectPage;
    login: ProspectPage;
    register: ProspectPage;
    resetPassword: ProspectPage;
    propertyFaq: ProspectPage;
    help: ProspectPage;
    aboutUs: ProspectPage;
    startTour: ProspectPage;
    unitmap: ProspectPage;
    propertyMap: ProspectPage;
    ask: ProspectPage;
} = {
    welcome: {
        extension: 'welcome',
        component: WelcomePage
    },
    properties: {
        extension: 'properties',
        component: PropertiesPage,
        displayText: 'View All Properties',
        icon: require('../../../images/view-all-properties.png')
    },
    tour24Faq: {
        extension: 'tour24Faq',
        component: Tour24FaqsPage,
        displayText: 'FAQ',
        icon: require('../../../images/faq-nav-icon.png')
    },
    webTourFaq: {
        extension: 'webTourFaq',
        component: WebTourFaqsPage,
        displayText: 'FAQ'
    },
    unitmap: {
        extension: 'map',
        component: UnitMapView,
        displayText: 'Tour Map',
        icon: require('../../../images/faq-nav-icon.png')
    },
    profile: {
        extension: 'profile',
        component: MyProfilePage,
        displayText: 'My Profile',
        icon: require('../../../images/my-profile.png')
    },
    tours: {
        extension: 'tour',
        component: MyToursPage,
        displayText: 'My Tours',
        icon: require('../../../images/my-tours.png')
    },
    startTour: {
        extension: 'starttour',
        component: StartTourPage,
        displayText: 'Start Tour',
        icon: require('../../../images/my-tours.png')
    },
    preferences: {
        extension: 'preferences',
        component: MyPreferencesPage,
        displayText: 'My Preferences',
        icon: require('../../../images/leasing-preferences.png')
    },
    login: {
        extension: 'login',
        component: LoginPage,
        displayText: 'Sign In',
        icon: require('../../../images/sign-in-nav-icon.png')
    },
    register: {
        extension: 'register',
        component: RegistrationPage,
        displayText: 'Sign In or Sign Up',
        icon: require('../../../images/sign-in-nav-icon.png')
    },
    resetPassword: {
        extension: 'reset',
        component: PasswordResetPage,
        displayText: 'Reset Password'
    },
    propertyFaq: {
        extension: 'propertyFaq',
        component: FaqPage,
        displayText: 'Property FAQs'
    },
    help: {
        extension: 'help',
        component: HelpPage,
        displayText: 'Help',
        icon: require('../../../images/help.png')
    },
    aboutUs: {
        extension: 'about',
        component: AboutUsPage,
        displayText: 'About Us',
        icon: require('../../../images/about-us-nav-icon.png')
    },
    propertyMap: {
        extension: 'property-map',
        component: PropertyMapPage,
        displayText: 'Map'
    },
    ask: {
        extension: 'ask',
        component: AskPage,
        displayText: 'Help'
    }
};

export const prospectPath = (page: ProspectPage) => `${ProspectBasePath}/${page.extension}`;

export interface ProspectRouterProps
    extends RouteComponentProps<{
        propertySlug: string;
        guestCardGuid?: string;
    }> {}

const ProspectRouter = (props: ProspectRouterProps) => {
    React.useEffect(() => {
        const queryData = queryString.parse(props.location.search) as SourceData;
        var existingData = getSourceData();
        const combinedSourceData = {
            ...existingData,
            ...queryData
        };
        setSourceData(combinedSourceData);
    }, []);

    return (
        <Switch>
            <Route
                path={prospectPath(prospectPages.welcome)}
                component={prospectPages.welcome.component}
            />

            <Route
                path={prospectPath(prospectPages.properties)}
                component={prospectPages.properties.component}
            />

            <Route
                path={`${prospectPath(prospectPages.unitmap)}/${propertySlugCapture}`}
                component={prospectPages.unitmap.component}
            />

            <ProspectLayout>
                <Switch>
                    <Route
                        path={prospectPath(prospectPages.tour24Faq)}
                        component={prospectPages.tour24Faq.component}
                    />
                    <Route
                        path={prospectPath(prospectPages.webTourFaq)}
                        component={prospectPages.webTourFaq.component}
                    />
                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={prospectPath(prospectPages.profile)}
                        component={prospectPages.profile.component}
                    />
                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={prospectPath(prospectPages.preferences)}
                        component={prospectPages.preferences.component}
                    />

                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={prospectPath(prospectPages.tours)}
                        component={prospectPages.tours.component}
                    />

                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={`${prospectPath(prospectPages.startTour)}/${tourGuidCapture}`}
                        component={prospectPages.startTour.component}
                    />

                    <Route path={prospectPath(prospectPages.login)} component={LoginPage} />

                    <Route
                        path={prospectPath(prospectPages.register)}
                        component={RegistrationPage}
                    />

                    <Route
                        path={prospectPath(prospectPages.resetPassword)}
                        component={PasswordResetPage}
                    />

                    <Route
                        path={`${prospectPath(prospectPages.propertyFaq)}/${propertySlugCapture}`}
                        component={FaqPage}
                    />

                    <Route path={prospectPath(prospectPages.help)} component={HelpPage} />

                    <Route path={prospectPath(prospectPages.aboutUs)} component={AboutUsPage} />
                    <Route
                        path={`${prospectPath(prospectPages.propertyMap)}/${propertySlugCapture}`}
                        component={PropertyMapPage}
                    />
                    <Route
                        path={`${prospectPath(prospectPages.ask)}/${propertySlugCapture}`}
                        component={AskPage}
                    />
                    <Route path={ProspectBasePath} exact={true} component={ProspectIndexPage} />
                </Switch>
            </ProspectLayout>
        </Switch>
    );
};

export default ProspectRouter;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/ProspectRouter.tsx